import React from "react";
import PageFrame from "./PageFrame";
import PageHeading from "./PageHeading";
import Abs from "./Abs";

/**
 * Page 10 - The Team.
 *
 * Sections of round headshots, eight to a row. The tile is 96 wide and the row divides the
 * grid exactly - 8 x 96 + 7 x 24 = 936, from x=62 to x=998 - with a 74px circle centred in
 * it. The template's own rows sit at y 139, 267, 384, 515 and 659 with section headings at
 * y 122, 248 and 495; laying the sections out in flow reproduces that spacing while
 * tolerating a roster that grows or shrinks.
 *
 * The page has moved away from the template in several deliberate steps, all at the
 * business's request:
 *
 *   2026-09-12  the flow starts at y=142 rather than 122, and sections sit apart rather than
 *               touching, so the groups read as separate blocks
 *   2026-09-18  eight tiles to a row rather than nine, and a 74px circle rather than 80
 *   2026-09-18  4px between rows, 20px between sections and 10px under each heading - the
 *               row break reading as half the pause of a section break
 *   2026-09-18  sections run straight on from one another and break only where the page ends,
 *               rather than moving to a fresh page whole
 *   2026-09-18  four rows to a page: the gaps above are sized to that, and the pagination
 *               stopped charging pages for a section margin they were never drawn
 */

/** Loading, failed and genuinely-empty must read differently. */
function emptyMessage(status, error) {
    if (status === 'error') {
        return error
            ? `Team members could not be loaded: ${error}`
            : 'Team members could not be loaded.';
    }
    if (status === 'loading') return 'Loading team members...';
    return 'No team members found.';
}

const GRID_X = 62;

/**
 * Where the first section heading sits.
 *
 * It was 122, six pixels under the intro line, which put LEADERSHIP against the standing
 * copy with no separation. 142 gives the same air above the first heading that now sits
 * between the sections themselves. Raised at the business's request on 2026-09-12.
 */
const GRID_TOP = 142;
const GRID_WIDTH = 936;

/**
 * Pagination.
 *
 * The roster outgrew one page - the last row used to print over the STRIVE mark at
 * y=786 - and interns add a fourth section, so the page now spills onto as many
 * continuation pages as the roster needs.
 *
 * The measurements below mirror the SCSS: a heading is 17px of line plus the 10px beneath it,
 * a row of tiles is 74 photo + 5 + 12 name + 16 title (two lines, as "TALENT ACQUISITION
 * COORDINATOR" needs at this width) + the 4px row gap, and `SECTION_GAP` is the
 * `+ .bov-team__section` margin. Keep these in step with PrintBOV.scss - understating any of
 * them lets the last row print over the STRIVE mark.
 *
 * The gaps are what decide how many rows reach a page. A page holds four rows only while
 * `3 x HEADING_H + 4 x ROW_H + 2 x SECTION_GAP` stays inside the 654px a continuation page
 * has; at a 16px row gap that came to 693 and the page took three, pushing the last section
 * onto a page of its own. 11 is the limit, 10 leaves 9px of margin for a substituted font.
 *
 * `SECTION_GAP` is counted only between sections that share a page, because that is the only
 * place the CSS draws it. Folding it into `HEADING_H` instead - as this did until
 * 2026-09-18 - charged every page 32px it never spent, and cost each one a row: the business
 * could see the space for a fourth row that the arithmetic said was not there.
 */
export const TEAM_ROW_SIZE = 8;
const HEADING_H = 27;
const SECTION_GAP = 20;
const ROW_H = 111;
const GRID_BOTTOM = 780;          // the STRIVE mark starts at y=786
/**
 * Where the first section heading sits on a continuation page, which carries no intro line.
 *
 * It was 106, seven pixels under the rule at y=98, so a heading landing at the top of a
 * second page - OPERATIONS & SUPPORT on the current roster - sat against the rule with no
 * air above it. 126 leaves 27px, matching the gap the first page now has between its intro
 * and LEADERSHIP, so a section reads the same whichever page it falls on.
 */
const CONT_GRID_TOP = 126;

/**
 * Splits the roster into per-page section lists. A section too tall for what is left of
 * a page breaks across pages and its remainder reprints the heading marked (CONTINUED), so a
 * long INVESTMENT TEAM never silently loses its tail.
 *
 * Always returns at least one page, so the empty/loading message still has somewhere to
 * print.
 */
export function paginateTeamSections(sections) {
    const pages = [];
    let current = [];
    let used = 0;
    let top = GRID_TOP;

    const flush = () => {
        if (!current.length) return;
        pages.push(current);
        current = [];
        used = 0;
        top = CONT_GRID_TOP;
    };

    (sections || []).forEach(section => {
        let members = section.members || [];
        let continued = false;

        // Every section starts where the one before it ended, and breaks only when the page
        // actually runs out.
        //
        // A section that would not fit in what was left used to move to a fresh page whole,
        // to avoid stranding a row or two overleaf. The business asked on 2026-09-18 for the
        // opposite: OPERATIONS & SUPPORT was starting a second page while most of the first
        // stood empty, and they would rather each section ran straight on and broke where it
        // had to, with the remainder headed "(CONTINUED)". The loop below does that on its
        // own - what it needed was for nothing to pre-empt it.

        while (members.length) {
            // The section margin is only drawn against a section above it on the same page,
            // so a block that opens a page is not charged for one.
            const lead = current.length ? SECTION_GAP : 0;
            const available = (GRID_BOTTOM - top) - used - lead - HEADING_H;
            let rows = Math.floor(available / ROW_H);
            if (rows < 1) {
                // Nothing fits here, but a page must always take at least one row or the
                // roster would never drain.
                if (current.length) {
                    flush();
                    continue;
                }
                rows = 1;
            }

            const take = members.slice(0, rows * TEAM_ROW_SIZE);
            current.push({section: section.section, members: take, continued});
            used += lead + HEADING_H + Math.ceil(take.length / TEAM_ROW_SIZE) * ROW_H;
            members = members.slice(take.length);
            continued = true;
            if (members.length) flush();
        }
    });

    flush();
    return pages.length ? pages : [[]];
}

/**
 * Standing copy under the rule. Fixed text, not agent-editable, so it lives here rather
 * than in bov-boilerplate with the Value Analysis blocks.
 */
const INTRO = 'Every assignment includes at least one Senior agent and our entire operations '
    + 'team: Research, Transaction Management, Marketing, Data Integrity, and Financial '
    + 'Consulting, all working in concert to meet your goals.';

export default function Team({stats, teamStatus, teamError, pageNo, totalPages, teamChunk, continued}) {
    // `teamChunk` is one page's worth from paginateTeamSections; the whole roster is the
    // fallback for any caller that renders the page on its own.
    const sections = teamChunk || stats.teamSections || [];

    return (
        <PageFrame fixed pageNo={pageNo} totalPages={totalPages}>
            <PageHeading
                eyebrow='THE TEAM'
                title='Multiple Pros. '
                titleAccent='One Team.'
            />

            <Abs x={48} y={98} w={960} className='bov-rule'/>

            {/*
              * Two right-aligned lines ending on the content margin, sitting at y 58 and
              * 75 as in the template.
              *
              * The separator is a literal bullet between ordinary spaces, exactly as the
              * page eyebrows write it. It must not be `&nbsp;&bull;&nbsp;`: GothamBook has
              * neither glyph, so all three characters fell back to a synthesised font that
              * measured the separator alone at 585px, which is what pushed the line into a
              * third row. Ordinary spaces cannot break the line either, because the kicker
              * is nowrap.
              */}
            <Abs x={708} y={56} w={300}>
                <div className='bov-team__kicker'>
                    STRIVE<span className='bov-team__dot'>&#8226;</span>Leadership &amp; Operations
                </div>
                <div className='bov-team__kicker'>Headquartered in Dallas, Texas</div>
            </Abs>

            {continued ? null : (
                <Abs x={48} y={100} w={974}>
                    <div className='bov-team__intro'>{INTRO}</div>
                </Abs>
            )}

            <Abs x={GRID_X} y={continued ? CONT_GRID_TOP : GRID_TOP} w={GRID_WIDTH}>
                {sections.length ? (
                    sections.map(section => (
                        <div key={section.section} className='bov-team__section'>
                            <div className='bov-team__sectionTitle'>
                                {section.section}{section.continued ? ' (CONTINUED)' : ''}
                            </div>
                            <div className='bov-team__grid'>
                                {section.members.map((member, i) => (
                                    <div key={`${member.name}-${i}`} className='bov-team__member'>
                                        {member.photo
                                            ? <img className='bov-team__photo' src={member.photo} alt=''/>
                                            : <div className='bov-team__placeholder'/>}
                                        <div className='bov-team__name'>{member.name}</div>
                                        <div className='bov-team__title'>{member.title}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className='bov-team__empty'>{emptyMessage(teamStatus, teamError)}</div>
                )}
            </Abs>
        </PageFrame>
    );
}
